import React, { useCallback, useState } from 'react';
import { StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useResponsive, useSafeNavigation, useSafeRouter } from '@sg/garnish';

import { ORDER_AGAIN_TABS, OrderAgain } from '@order/features/orders';
import { useIsExpoRouterEnabled } from '@order/shared/hooks';
import { useTelemetry } from '@order/Telemetry';

import {
  FloatingButtons,
  SignedOutView,
  TransparentModal,
} from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';
import { useLastInteractedStore } from '../../context/LastInteractedStore';
import { useCart } from '../../hooks';
import type { ModalStackParamList } from '../../navigation/AppNavigation.props';
import { useReorderOrders } from './hooks';
import { useReorderOrdersV2 } from './hooks/useReorderOrdersV2';
import {
  ReorderScreenFavoritesTabPanel,
  ReorderScreenOrdersTabPanel,
} from './tabs';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A modal-screen that provides a simple user interface to reorder previous
 * orders and favorites.
 */
export const ReorderModal = () => {
  const navigation = useSafeNavigation();
  const router = useSafeRouter();

  const onBackHandler = useCallback(() => {
    if (navigation?.canGoBack()) {
      navigation.goBack();
      return;
    }

    if (router?.canGoBack()) {
      router.back();
      return;
    }

    router?.dismissTo('/');
  }, [navigation, router]);

  return (
    <TransparentModal
      onClose={onBackHandler}
      size="small"
      dialogMaxHeight={844}
    >
      <ReorderScreenContent
        canCloseScreen={true}
        showBagButtonV2={false}
        onBackHandler={onBackHandler}
      />
    </TransparentModal>
  );
};

/**
 * A tab-screen that provides a simple user interface to reorder previous
 * orders and favorites.
 */
export const ReorderTab = () => {
  return <ReorderScreenContent canCloseScreen={false} showBagButtonV2={true} />;
};

// ─────────────────────────────────────────────────────────────────────────────

const ReorderScreenContent = (props: ReorderScreenReadyProps) => {
  const isLoggedIn = useIsLoggedIn();

  if (!isLoggedIn) {
    return <SignedOutView withoutFooter />;
  }

  return <ReorderScreenReady {...props} />;
};

const ReorderScreenReady = (props: ReorderScreenReadyProps) => {
  const {
    canCloseScreen,
    showBagButtonV2,

    onBackHandler,
  } = props;

  const router = useSafeRouter();
  const { lineItemsCount } = useCart();
  const { match } = useResponsive();
  const { track } = useTelemetry();
  const isExpoRouterEnabled = useIsExpoRouterEnabled();

  // Get last interacted store as fallback
  const lastInteractedStore = useLastInteractedStore();

  const currentRestaurantId = lastInteractedStore?.restaurantId
    ? Number.parseInt(lastInteractedStore.restaurantId, 10)
    : undefined;

  // ─── Navigation ──────────────────────────────────────────────────────

  const modalNavigation = useSafeNavigation<ModalStackParams>();

  // NOTE: Reorder modal is represented as a small modal on large breakpoints
  // and a fullscreen one on the smallest breakpoint which introduces some issues
  // We use different methods of navigation per breakpoint to avoid unexpected
  // issues.
  const navigationMethod = match([
    modalNavigation?.navigate,
    modalNavigation?.replace,
  ]);
  const expoNavigationMethod = match([router?.navigate, router?.replace]);

  const navigateToBag = useCallback(() => {
    if (isExpoRouterEnabled) {
      expoNavigationMethod?.('/bag');
      return;
    }

    navigationMethod?.('Bag');
  }, [isExpoRouterEnabled, navigationMethod, expoNavigationMethod]);

  // ─── Remote Data ─────────────────────────────────────────────────────

  // Always call both hooks to follow React rules, then conditionally use the result

  const reorderOrdersV1 = useReorderOrders();
  const reorderOrdersV2 = useReorderOrdersV2(currentRestaurantId);

  const reorderOrders = currentRestaurantId ? reorderOrdersV2 : reorderOrdersV1;

  // ─── State ───────────────────────────────────────────────────────────

  const [activeTabId, setActiveTabId] = useState(ORDER_AGAIN_TABS.ORDERS.ID);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onTabChange = useCallback(
    (tabId: string) => {
      if (tabId === ORDER_AGAIN_TABS.ORDERS.ID) {
        track('reorder.orders.tab.tapped');
      }

      if (tabId === ORDER_AGAIN_TABS.FAVORITES.ID) {
        track('reorder.favorites.tab.tapped');
      }

      setActiveTabId(tabId);
    },
    [track],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <OrderAgain.Header shouldUseSafeArea={false}>
        <OrderAgain.HeaderTitle />

        {canCloseScreen && onBackHandler ? (
          <OrderAgain.CloseButton onPress={onBackHandler} />
        ) : null}
      </OrderAgain.Header>

      <OrderAgain.Tabs activeTabId={activeTabId} setActiveTabId={onTabChange} />

      <ReorderScreenOrdersTabPanel
        activeTabId={activeTabId}
        reorderOrders={reorderOrders}
      />

      <ReorderScreenFavoritesTabPanel
        activeTabId={activeTabId}
        mostRecentOrder={reorderOrders.mostRecentOrder}
        restaurantId={currentRestaurantId}
      />

      {lineItemsCount > 0 && showBagButtonV2 ? (
        <FloatingButtons.Container anchor="right">
          <FloatingButtons.Bag showMessage={false} onPress={navigateToBag} />
        </FloatingButtons.Container>
      ) : null}

      {lineItemsCount > 0 && !showBagButtonV2 ? (
        <OrderAgain.BagButton
          lineItemsCount={lineItemsCount}
          onPress={navigateToBag}
        />
      ) : null}
    </SafeAreaView>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ModalStackParams = NativeStackNavigationProp<ModalStackParamList>;

type ReorderScreenReadyProps = {
  canCloseScreen: boolean;
  showBagButtonV2: boolean;
  onBackHandler?: () => void;
};
